import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { catchError, concatMap, mergeMap } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  SwapAuthForAccessTokens
} from '../../models/login';
import { ChallengeScreenError } from '../../services/security-verification/challenge-success.service';
import { InitiateLoginFacadeService } from './initiate-login-facade.service';
import { PostWithCredentialsFacadeHelperService } from './post-with-credentials-facade-helper.service';

type ErrorResponse =
  | LoginOAuthResponse
  | LoginAuthWithCredentialsResponse
  | SwapAuthForAccessTokens
  | HttpErrorResponse
  | ChallengeScreenError
  | string;

@Injectable({ providedIn: 'root' })
export class PostWithCredentialsFacadeService extends PostWithCredentialsFacadeHelperService {
  private retryCount = 0;

  postWithCredFlow(
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    if (this.pkceCode.codeChallenge && this.pkceCode.codeVerifier) {
      loginOAuthParams.pkceCode = this.pkceCode;
    }
    if (!this.flowId) {
      return of(
        this.LoginRememberMeErrorUtil.checkRememberMeErrors(
          loginOAuthParams.oauthConfig,
          'ERROR_MSG_NO_FLOW_ID_GENERATED'
        )
      );
    }
    return this.loginPostWithCred
      .loginPostWithCredentials(
        loginOAuthParams.oauthConfig,
        this.flowId,
        requestInput
      )
      .pipe(
        concatMap((response: LoginAuthWithCredentialsResponse) =>
          this.handleCredentialsResponse(
            response,
            loginOAuthParams,
            requestInput
          )
        ),
        concatMap((response: LoginAuthWithCredentialsResponse) =>
          this.handleRememberMeResponse(response, loginOAuthParams)
        ),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens
          ) => this.processSwapResponse(response, loginOAuthParams)
        ),
        concatMap((response) =>
          this.processTokenResponse(response, loginOAuthParams)
        ),
        catchError((error) =>
          this.handleErrorWithRetry(error, loginOAuthParams, requestInput)
        )
      );
  }

  private handleErrorWithRetry(
    error: ErrorResponse,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    if (typeof error === 'string') return of(error);

    const retryTimer = loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER || 1000;
    const maxRetryCount = loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT || 0;

    return timer(retryTimer).pipe(
      mergeMap(() => {
        this.retryCount++;
        if (this.retryCount !== maxRetryCount) {
          loginOAuthParams.pkceCode =
            this.loginRememberMeUtils.generateCodeChallenge();
          return this.handlePostWithCredError(
            error,
            loginOAuthParams,
            requestInput
          );
        }
        this.retryCount = 0;
        return of(
          this.LoginRememberMeErrorUtil.checkRememberMeErrors(
            loginOAuthParams.oauthConfig,
            error
          )
        );
      })
    );
  }

  private handlePostWithCredError(
    error: ErrorResponse,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    const initiateLoginFacade = this.injector.get(InitiateLoginFacadeService);
    if (
      !loginOAuthParams.pkceCode.codeChallenge &&
      !loginOAuthParams.pkceCode.codeVerifier
    ) {
      loginOAuthParams.pkceCode =
        this.loginRememberMeUtils.generateCodeChallenge();
    }
    if (this.shouldRetryLogin(error)) {
      return initiateLoginFacade.initiateLogin(loginOAuthParams, requestInput);
    }
    return of(
      this.LoginRememberMeErrorUtil.checkRememberMeErrors(
        loginOAuthParams.oauthConfig,
        error
      )
    );
  }
}
